import axios from 'axios'
import {useEffect, useRef, useState} from 'react'
import {API_URL} from '../../../../../_metronic/helpers'
import {
  SearchSelect,
  SearchSelectOption,
} from '../../../../../_metronic/helpers/components/SearchSelect'
import {AuthModel, useAuth} from '../../../auth'
import {confirmLeavingUnsavedWork} from '../business-switcher/unsavedWork'
import {StaffSessionModel, staffWithoutBusiness} from '../../../auth/core/staffAccess'

type CompanyScopedUser = {
  rl?: number
  cpi?: number
  staff?: StaffSessionModel | null
}

// Staff who pick any business may have none selected (cpi 0); per-business lists must wait for one.
const isAdminWithoutSelectedCompany = (user?: CompanyScopedUser | null) =>
  staffWithoutBusiness(user)

const readChangeCompanyError = (error: any) => {
  const status = error?.response?.status
  if (status === 403) {
    return 'دسترسی به این کسب‌وکار مجاز نیست.'
  }

  const data = error?.response?.data
  const message =
    data?.Message ||
    data?.ExceptionMessage ||
    (typeof data === 'string' ? data : '') ||
    (error?.response ? '' : error?.message)

  return message || 'تغییر کسب‌وکار انجام نشد. دوباره تلاش کنید.'
}

type Props = {
  className?: string
  placeholder?: string
  onError?: (message: string) => void
  // Runs once the server accepted the business, just before the page reloads with it.
  onSelected?: (companyId: number) => void
}

const AdminCompanySelector = ({
  className,
  placeholder = 'انتخاب کسب‌وکار...',
  onError,
  onSelected,
}: Props) => {
  const {currentUser, saveAuth, setCurrentUser} = useAuth()
  const [company, setCompany] = useState<SearchSelectOption>()
  const [isChanging, setIsChanging] = useState(false)
  const [error, setError] = useState('')
  const changingRef = useRef(false)
  const confirmedCompanyRef = useRef<SearchSelectOption>()
  const onSelectedRef = useRef(onSelected)
  onSelectedRef.current = onSelected
  const selectedCompanyId = Number(currentUser?.cpi || 0)

  useEffect(() => {
    const nextCompanyId = Number(company?.value || 0)
    if (nextCompanyId && nextCompanyId === selectedCompanyId) {
      confirmedCompanyRef.current = company
    }
    // Clearing the combo (react-select sends null) while a business is selected returns the admin
    // to all businesses (compnyId 0), the same as clearing the header switcher.
    const clearedSelectedCompany = company === null && selectedCompanyId > 0
    if (
      (!nextCompanyId && !clearedSelectedCompany) ||
      nextCompanyId === selectedCompanyId ||
      changingRef.current
    ) {
      return
    }

    if (!confirmLeavingUnsavedWork()) {
      setCompany(confirmedCompanyRef.current)
      return
    }

    changingRef.current = true
    setIsChanging(true)
    setError('')
    axios
      .post(`${API_URL}/auth/changeCompany`, {
        compnyId: nextCompanyId,
      })
      .then((response) => {
        const auth = response.data as AuthModel
        onSelectedRef.current?.(nextCompanyId)
        saveAuth(auth)
        setCurrentUser(auth)
        window.location.reload()
      })
      .catch((requestError) => {
        const message = readChangeCompanyError(requestError)
        changingRef.current = false
        setIsChanging(false)
        setCompany(confirmedCompanyRef.current)
        if (onError) {
          onError(message)
        } else {
          setError(message)
        }
      })
  }, [company, onError, saveAuth, selectedCompanyId, setCurrentUser])

  return (
    <div className={className} aria-busy={isChanging}>
      <SearchSelect
        section='adminCompany'
        placeholder={placeholder}
        defaultvalue={selectedCompanyId > 0 ? selectedCompanyId : undefined}
        isDisabled={isChanging}
        error={error || undefined}
        stateValue={{
          value: company,
          setValue: setCompany,
        }}
      />
    </div>
  )
}

export {AdminCompanySelector, isAdminWithoutSelectedCompany}
